import React, { useState, useRef } from "react";
import { Modal, Button, Form, Table, Spinner, Badge } from "react-bootstrap";
import { FiUpload, FiFile, FiCheckCircle } from "react-icons/fi";
import axios from "axios";
import { toast } from "react-toastify";

const UploadModal = ({ show, onHide, onUploadSuccess }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [summary, setSummary] = useState(null);
    const fileInputRef = useRef(null);

    const COLUMN_MAPPING = {
        PAYEE_NAME: 10,
        ACCOUNT_NUMBER: 9,
        STATUS: 31,
        PAYMENT_DATE: 33,
    };

    const STATUS_MAPPING = {
        '0': 'Completed',
        '1': 'Rejected',
        '2': 'Received',
        '3': 'Cancelled',
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        const fileExtension = selectedFile.name.toLowerCase();
        const isValidExtension = fileExtension.match(/\.(xlsx|xls|csv|txt)$/);

        if (!isValidExtension) {
            toast.error("Please select a valid Excel or text file");
            return;
        }

        const maxSize = 10 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
            toast.error(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
            e.target.value = '';
            return;
        }

        setFile(selectedFile);
        setPreviewData([]);
        setHeaders([]);
        setTotalRows(0);
        setSummary(null);
        parseFile(selectedFile);
    };

    const parseFile = async (file) => {
        const fileExtension = file.name.toLowerCase().split('.').pop();

        try {
            if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                await parseExcelFile(file);
            } else if (fileExtension === 'csv' || fileExtension === 'txt') {
                await parseCSVFile(file);
            } else {
                toast.error(`Unsupported file format: .${fileExtension}`);
                return;
            }
        } catch (error) {
            toast.error("Error parsing file: " + error.message);
        }
    };

    const parseExcelFile = async (file) => {
        try {
            const XLSX = await import('xlsx');
            const reader = new FileReader();

            reader.onload = function (e) {
                try {
                    const fileData = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(fileData, {
                        type: 'array',
                        cellDates: true,
                        raw: false
                    });

                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    if (!worksheet['!ref']) {
                        toast.error("No data found in the worksheet");
                        return;
                    }

                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        defval: '',
                        blankrows: false
                    });

                    processFileData(jsonData, file.name);

                } catch (error) {
                    toast.error("Error processing Excel file: " + error.message);
                }
            };

            reader.onerror = function (error) {
                toast.error("Error reading file: " + error.message);
            };

            reader.readAsArrayBuffer(file);
        } catch (error) {
            toast.error("Error loading Excel parser: " + error.message);
        }
    };

    const parseCSVFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = function (e) {
                try {
                    const content = e.target.result;
                    const lines = content.split(/\r\n|\n|\r/);
                    const jsonData = [];

                    let delimiter = ',';
                    const firstLine = lines[0] || '';

                    if (firstLine.includes('\t')) delimiter = '\t';
                    else if (firstLine.includes(';')) delimiter = ';';
                    else if (firstLine.includes('|')) delimiter = '|';

                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (line === '') continue;

                        const row = parseCSVLine(line, delimiter);
                        jsonData.push(row);
                    }

                    if (jsonData.length === 0) {
                        toast.error("File is empty");
                        reject(new Error("Empty file"));
                        return;
                    }

                    processFileData(jsonData, file.name);
                    resolve();

                } catch (error) {
                    toast.error("Error processing CSV file: " + error.message);
                    reject(error);
                }
            };

            reader.onerror = function (error) {
                toast.error("Error reading CSV file: " + error.message);
                reject(error);
            };

            reader.readAsText(file, 'UTF-8');
        });
    };

    const parseCSVLine = (line, delimiter) => {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === delimiter && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        return result;
    };

    const processFileData = (jsonData, fileName) => {
        if (jsonData.length === 0) {
            toast.error("No data found in file");
            return;
        }

        const dataRows = jsonData.slice(1, -1);

        if (dataRows.length === 0) {
            toast.error("No data rows after skipping first and last row");
            return;
        }

        const maxColumnIndex = Math.max(
            COLUMN_MAPPING.PAYEE_NAME,
            COLUMN_MAPPING.ACCOUNT_NUMBER,
            COLUMN_MAPPING.STATUS,
            COLUMN_MAPPING.PAYMENT_DATE
        ) + 1;

        const displayHeaders = [];
        for (let i = 0; i < maxColumnIndex; i++) {
            displayHeaders.push(`Column ${i + 1}`);
        }

        const parsedData = [];

        for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];
            if (!row || !Array.isArray(row)) continue;

            const payeeName = getValueFromColumn(row, COLUMN_MAPPING.PAYEE_NAME);
            const accountNumber = getValueFromColumn(row, COLUMN_MAPPING.ACCOUNT_NUMBER);
            const status = getValueFromColumn(row, COLUMN_MAPPING.STATUS);
            const paymentDate = getValueFromColumn(row, COLUMN_MAPPING.PAYMENT_DATE);

            const hasData = payeeName || accountNumber || status || paymentDate;

            if (hasData) {
                const formattedStatus = formatStatus(status);
                const formattedDate = extractDateFromString(paymentDate);

                const rowData = {};
                displayHeaders.forEach((header, colIndex) => {
                    rowData[header] = row[colIndex] !== undefined ? String(row[colIndex]).trim() : '';
                });

                rowData._extractedData = {
                    payeeName,
                    accountNumber,
                    status: formattedStatus,
                    paymentDate: formattedDate,
                };
                rowData._rowNumber = i + 2;

                parsedData.push(rowData);
            }
        }

        const summaryData = generateSummary(parsedData);

        setHeaders(displayHeaders);
        setTotalRows(parsedData.length);
        setPreviewData(parsedData.slice(0, 25));
        setSummary(summaryData);

        if (parsedData.length === 0) {
            toast.warning("No valid payment data found in the file");
        } else {
            toast.success(`Processed ${parsedData.length} payment records from ${fileName}`);
        }
    };

    const getValueFromColumn = (row, columnIndex) => {
        if (!row || !Array.isArray(row)) return '';
        if (columnIndex >= row.length) return '';

        const value = row[columnIndex];
        if (value === null || value === undefined) return '';

        return String(value).trim();
    };

    const formatStatus = (status) => {
        if (!status) return 'Unknown';

        const statusStr = String(status).trim();

        if (STATUS_MAPPING[statusStr]) {
            return STATUS_MAPPING[statusStr];
        }

        const fallbackMap = {
            'success': 'Completed',
            'fail': 'Failed',
            'failed': 'Failed',
            'pending': 'Pending',
            'processing': 'Processing',
            'cancelled': 'Cancelled',
            'rejected': 'Rejected',
            'received': 'Received'
        };

        const lowerStatus = statusStr.toLowerCase();
        return fallbackMap[lowerStatus] || statusStr;
    };

    const extractDateFromString = (dateString) => {
        if (!dateString) return '';

        const str = String(dateString).trim();

        const dateMatch = str.match(/\b(\d{1,2}-[A-Za-z]{3}-\d{4})\b/);
        if (dateMatch) {
            return dateMatch[1];
        }

        const datePatterns = [
            /\b(\d{1,2}\/\d{1,2}\/\d{4})\b/,
            /\b(\d{4}-\d{1,2}-\d{1,2})\b/,
            /\b(\d{1,2}\.\d{1,2}\.\d{4})\b/,
            /\b([A-Za-z]{3,9} \d{1,2}, \d{4})\b/,
        ];

        for (const pattern of datePatterns) {
            const match = str.match(pattern);
            if (match) {
                return match[1];
            }
        }

        return str.length > 50 ? str.substring(0, 50) + '...' : str;
    };

    const generateSummary = (data) => {
        if (!data.length) return null;

        const summaryArray = data.map(row => {
            const extracted = row._extractedData;
            return {
                payeeName: extracted.payeeName || 'N/A',
                paymentDate: extracted.paymentDate || 'N/A',
                accountNumber: extracted.accountNumber || 'N/A',
                status: extracted.status || 'N/A'
            };
        });

        return summaryArray;
    };

    const handleUpload = async () => {
        if (!file || previewData.length === 0) {
            toast.error("No data to upload");
            return;
        }

        setUploading(true);

        try {
            const uploadPayload = {
                summary: summary,
            };

            const response = await axios.post(
                'https://www.azzenterprises.com/api/reports/upload-reports',
                uploadPayload,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true,
                    timeout: 30000
                }
            );

            if (response.data.success) {
                toast.success(`Successfully uploaded ${response.data.processed} records`);
                onUploadSuccess();
                handleClose();
            } else {
                toast.error(response.data.message || "Upload failed");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Upload failed";
            toast.error(errorMessage);
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        setHeaders([]);
        setTotalRows(0);
        setSummary(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onHide();
    };

    const getFileIcon = () => {
        if (!file) return <FiFile size={16} />;
        if (file.name.match(/\.(xlsx|xls)$/)) {
            return <FiFile size={16} className="text-success" />;
        } else {
            return <FiFile size={16} className="text-primary" />;
        }
    };

    const truncateText = (text, maxLength = 50) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const getStatusVariant = (status) => {
        if (!status) return 'secondary';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('completed') || statusLower === '0') {
            return 'success';
        } else if (statusLower.includes('rejected') || statusLower.includes('failed') || statusLower.includes('cancelled')) {
            return 'danger';
        } else if (statusLower.includes('pending') || statusLower.includes('processing') || statusLower.includes('received')) {
            return 'warning';
        }
        return 'secondary';
    };

    const shouldShowSummary = summary && totalRows > 0;

    const isHighlightedColumn = (columnIndex) => {
        return Object.values(COLUMN_MAPPING).includes(columnIndex);
    };

    const getColumnLabel = (columnIndex) => {
        if (columnIndex === COLUMN_MAPPING.PAYEE_NAME) return "Payee Name";
        if (columnIndex === COLUMN_MAPPING.ACCOUNT_NUMBER) return "Account Number";
        if (columnIndex === COLUMN_MAPPING.STATUS) return "Status";
        if (columnIndex === COLUMN_MAPPING.PAYMENT_DATE) return "Payment Date";
        return `Column ${columnIndex + 1}`;
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header
                closeButton
                style={{
                    fontSize: "16px",
                    padding: "1rem",
                    position: 'sticky',
                    top: 0,
                    zIndex: 1050,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: "18px" }}>
                    <FiUpload size={20} />
                    Upload Payment Reports
                </Modal.Title>
            </Modal.Header>

            <Modal.Body
                style={{
                    fontSize: "14px",
                    padding: "1.5rem",
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}
            >
                <div className="mb-4">
                    <Form.Group>
                        <Form.Label className="fw-semibold" style={{ fontSize: "14px" }}>Select File</Form.Label>
                        <Form.Control
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv,.txt"
                            onChange={handleFileChange}
                            disabled={uploading}
                            style={{ fontSize: "14px" }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                            Supported formats: Excel (.xlsx, .xls), CSV (.csv), Text (.txt)
                        </Form.Text>
                    </Form.Group>
                </div>

                {file && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            {getFileIcon()}
                            <span className="fw-semibold" style={{ fontSize: "14px" }}>{file.name}</span>
                            <Badge bg="secondary" style={{ fontSize: "12px" }}>
                                {(file.size / 1024).toFixed(2)} KB
                            </Badge>
                        </div>
                        {headers.length > 0 && (
                            <div style={{ fontSize: "12px" }} className="text-muted">
                                <strong>Detected:</strong> {headers.length} columns × {totalRows} rows
                            </div>
                        )}
                    </div>
                )}

                {shouldShowSummary && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <FiCheckCircle className="text-primary" size={16} />
                            <span className="fw-semibold" style={{ fontSize: "14px" }}>Upload Summary</span>
                            <Badge bg="primary" style={{ fontSize: "12px" }}>
                                {summary.length} records
                            </Badge>
                        </div>
                        <div style={{ fontSize: "12px" }} className="text-muted mb-2">
                            <strong>Format:</strong> Payee Name - Payment Date - Account Number - Status
                        </div>
                        <div style={{ maxHeight: '200px', overflow: 'auto' }} className="fw-bold">
                            {summary.map((item, index) => {
                                const statusVariant = getStatusVariant(item.status);
                                return (
                                    <div key={index} className="border-bottom pb-1 mb-1" style={{ fontSize: "13px" }}>
                                        <span className="text-muted">{index + 1}. </span> {item.payeeName || 'No Name'} - {item.paymentDate || 'No Date'} - {item.accountNumber || 'No Account'} - <Badge bg={statusVariant} style={{ fontSize: "11px" }}>{item.status || 'No Status'}</Badge>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {previewData.length > 0 && headers.length > 0 && (
                    <div className="mb-3">
                        <h6 className="fw-semibold mb-2" style={{ fontSize: "14px" }}>
                            Data Preview ({previewData.length} of {totalRows} rows)
                        </h6>
                        <div style={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                            <Table striped bordered hover size="sm" responsive style={{ fontSize: "12px", margin: 0 }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                    <tr>
                                        <th style={{ fontSize: "12px", width: '60px', backgroundColor: '#f8f9fa', fontWeight: '600' }}>
                                            #
                                        </th>
                                        {headers.map((header, index) => (
                                            <th
                                                key={index}
                                                className="text-nowrap"
                                                style={{
                                                    fontSize: "12px",
                                                    backgroundColor: isHighlightedColumn(index) ? '#e7f3ff' : '#f8f9fa',
                                                    fontWeight: '600',
                                                    minWidth: '120px',
                                                    borderLeft: isHighlightedColumn(index) ? '2px solid #0d6efd' : '1px solid #dee2e6'
                                                }}
                                                title={isHighlightedColumn(index) ? getColumnLabel(index) : ''}
                                            >
                                                {isHighlightedColumn(index) ? (
                                                    <span className="text-primary">
                                                        {getColumnLabel(index)}
                                                    </span>
                                                ) : (
                                                    header
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((row, index) => (
                                        <tr key={index}>
                                            <td className="fw-semibold text-center" style={{ fontSize: "12px", backgroundColor: '#f8f9fa' }}>
                                                {row._rowNumber}
                                            </td>
                                            {headers.map((header, colIndex) => (
                                                <td
                                                    key={colIndex}
                                                    style={{
                                                        fontSize: "12px",
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: isHighlightedColumn(colIndex) ? '#f0f8ff' : 'transparent',
                                                        borderLeft: isHighlightedColumn(colIndex) ? '2px solid #0d6efd' : '1px solid #dee2e6'
                                                    }}
                                                    title={row[header] || ''}
                                                >
                                                    {truncateText(row[header])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {previewData.length < totalRows && (
                            <div className="text-muted text-center mt-2" style={{ fontSize: "12px" }}>
                                Showing first {previewData.length} rows of {totalRows} total rows
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer
                style={{
                    fontSize: "14px",
                    padding: "1rem 1.5rem",
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1050,
                    backgroundColor: 'white',
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    borderTop: '1px solid #dee2e6'
                }}
            >
                <Button variant="outline-secondary" onClick={handleClose} disabled={uploading} style={{ fontSize: "14px" }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleUpload} disabled={!file || uploading || previewData.length === 0} className="d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                    {uploading ? (
                        <>
                            <Spinner animation="border" size="sm" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <FiUpload size={16} />
                            Upload {totalRows} Records
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UploadModal;
